/**
 * Settings API Client
 *
 * API endpoints for user settings, preferences, and API key management
 */

import { apiClient } from './client';
import type {
  UserSettings,
  UpdateUserPreferencesRequest,
  ApiKey,
  ApiKeyCreateRequest,
  ApiKeyResponse,
} from '@/features/settings/types/settings.types';

/**
 * Get user settings and preferences
 */
export const getUserSettings = async (): Promise<UserSettings> => {
  const response = await apiClient.get<UserSettings>('/settings');
  return response.data;
};

/**
 * Update user preferences
 */
export const updateUserPreferences = async (
  data: UpdateUserPreferencesRequest
): Promise<UserSettings> => {
  const response = await apiClient.patch<UserSettings>('/settings/preferences', data);
  return response.data;
};

/**
 * List user's API keys
 */
export const listApiKeys = async (): Promise<ApiKey[]> => {
  const response = await apiClient.get<ApiKey[]>('/settings/api-keys');
  return response.data;
};

/**
 * Create new API key
 */
export const createApiKey = async (
  data: ApiKeyCreateRequest
): Promise<ApiKeyResponse> => {
  const response = await apiClient.post<ApiKeyResponse>('/settings/api-keys', data);
  return response.data;
};

/**
 * Revoke API key
 */
export const revokeApiKey = async (keyId: string): Promise<void> => {
  await apiClient.delete(`/settings/api-keys/${keyId}`);
};

/**
 * Regenerate API key
 */
export const regenerateApiKey = async (keyId: string): Promise<ApiKeyResponse> => {
  const response = await apiClient.post<ApiKeyResponse>(
    `/settings/api-keys/${keyId}/regenerate`
  );
  return response.data;
};

/**
 * Change the logged-in user's password.
 *
 * POSTs to `/auth/change-password` (apiClient prepends `/api/v1`). The server
 * verifies the current password, enforces strength rules on the new one, and
 * revokes the user's other sessions while keeping the current one.
 */
export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResult {
  success: boolean;
  message: string;
}

export const changePassword = async (
  input: ChangePasswordInput
): Promise<ChangePasswordResult> => {
  const response = await apiClient.post<ChangePasswordResult>(
    '/auth/change-password',
    {
      current_password: input.currentPassword,
      new_password: input.newPassword,
    }
  );
  return response.data;
};

export const settingsApi = {
  getUserSettings,
  updateUserPreferences,
  listApiKeys,
  createApiKey,
  revokeApiKey,
  regenerateApiKey,
  changePassword,
};
